import { DatePicker, Form, Input, Modal, Select, Switch, Tabs } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { SaveButton } from "~/components/buttons/SaveButton.jsx";
import { SluitButton } from "~/components/buttons/SluitButton.jsx";
import { clubHasFinance, clubHasFlight } from "~/featureMatrix.jsx";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { toLocaleDate } from "~/lib/localize.js";
import { ChecklistEditor } from "../dayTemplate/checklistTemplateEditor.jsx";
import { DienstTemplate } from "../dayTemplate/dienstTemplate.jsx";
import { RegistrationListTemplate } from "../dayTemplate/registrationListTemplate.jsx";
import { FlightLocationFields } from "./FlightLocationFields.jsx";
import { RemoveDayButton } from "./removeDayButton.jsx";

export function AddDayModal({
  profile,
  activeDay,
  club,
  visible,
  onCancel,
  onOk,
  confirmLoading,
  link_to,
  history,
  existingLijsten = null,
}) {
  if (!visible) return null;
  const [form] = Form.useForm();

  const [atThuisveld, setAtThuisVeld] = useState(true);
  const [vliegend, setVliegend] = useState(true);

  const wijzigRooster = profile?.wijzig_rooster;

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [diensten, setDienstenState] = useState([]);
  const [registration_lists, setRegistrationListsState] = useState([]);
  const [checklistStart, setChecklistStart] = useState([]);
  const [checklistEnd, setChecklistEnd] = useState([]);
  useEffect(() => {
    existingLijsten !== null && setRegistrationListsState(existingLijsten);
  }, [existingLijsten]);
  useEffect(() => {
    if (!isNew && visible) {
      setChecklistStart(activeDay?.checklist_start || []);
      setChecklistEnd(activeDay?.checklist_end || []);
    }
  }, [activeDay, visible]);

  // Templates can be done only when day is new
  const isNew = activeDay == null;
  const [templates, setTemplatesState] = useState([]);
  const fetchTemplates = async () => {
    const data = await api.get("days/day_templates.json");
    if (data) {
      setTemplatesState(
        data.map((d) => ({
          ...d,
          id: `${d.id}`,
          registration_lists: d?.registration_lists?.map((list, index) => ({
            ...list,
            id: `new_${index}`,
          })),
          roosters: d?.diensten?.map((dienst, index) => ({
            ...dienst,
            id: `new_${index}`,
          })),
        })),
      );
    }
  };
  useEffect(() => {
    if (visible) {
      fetchTemplates();
    }
  }, [visible, activeDay]);

  const vliegveld = !club
    ? ""
    : club.vliegveld_icao
      ? club.vliegveld_icao
      : club.vliegveld;

  useEffect(() => {
    if (activeDay && !activeDay.is_vliegend) {
      setAtThuisVeld(false);
    } else {
      setAtThuisVeld(
        !activeDay || !club || activeDay.vertrek_vliegveld === vliegveld,
      );
    }

    setVliegend(
      clubHasFlight() ? (!activeDay ? true : activeDay?.is_vliegend) : false,
    );
  }, [activeDay, visible]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const vertrek_vliegveld =
        atThuisveld || !values.locatie ? vliegveld : values.locatie;

      await onOk({
        ...activeDay,
        ...values,
        type_dag: isNew ? selectedTemplate?.name : values.type_dag,
        datum: activeDay ? activeDay.datum : values.datum.format("YYYY-MM-DD"),
        briefing_tijd: values.briefing_tijd,
        aankomst_vliegveld: vertrek_vliegveld || "-",
        vertrek_vliegveld: vertrek_vliegveld || "-",
        notify_users: !!activeDay,
        is_vliegend: !clubHasFlight() ? false : vliegend,
        diensten: diensten.filter((d) => !d.deleted),
        registration_lists: registration_lists.filter((d) => !d.deleted),
        ...(isNew
          ? {
              checklist_start_day: checklistStart,
              checklist_end_day: checklistEnd,
            }
          : { checklist_start: checklistStart, checklist_end: checklistEnd }),
      });
      form.resetFields();
    } catch (info) {
      console.log("Validate Failed:", info);
    }
  };

  return (
    <Modal
      title={
        activeDay
          ? toLocaleDate(new Date(activeDay.datum))
          : $t("Voeg nieuwe dag toe")
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        activeDay && (
          <RemoveDayButton
            key="verwijder"
            disabled={false}
            dag_id={activeDay.dag_id}
            history={history}
          />
        ),

        <SaveButton
          key="opslaan"
          onClick={handleSave}
          loading={confirmLoading}
        />,
        <SluitButton key="sluit" onClick={onCancel} />,
      ]}
    >
      <Form
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        name="addDay"
        preserve={false}
        requiredMark={false}
        initialValues={{
          start_methode: "lier",
          ...activeDay,
          datum: activeDay ? dayjs(activeDay.datum) : dayjs(),
          briefing_tijd: activeDay ? activeDay.briefing_tijd : "09:00",
          locatie: activeDay ? activeDay.vertrek_vliegveld : vliegveld,
          accept_vouchers: true,
        }}
      >
        {link_to && (
          <Form.Item
            label={$t("Datum")}
            name="datum"
            rules={[{ required: true, message: $t("Voeg datum toe!") }]}
          >
            <DatePicker
              format={localStorage.localeFormat}
              disabledDate={(current) => {
                if (link_to === "start")
                  return current && current > dayjs().startOf("day");
                return current && current < dayjs().startOf("day");
              }}
            />
          </Form.Item>
        )}

        <Form.Item
          label={$t("Soort dag")}
          extra={
            isNew
              ? $t(
                  "Bij het aanmaken worden diensten en aanmeldlijsten vanuit het template ingevuld",
                )
              : ""
          }
          name="type_dag"
          rules={[{ required: true, message: $t("Selecteer type") }]}
        >
          <Select
            placeholder={$t("Selecteer een template")}
            onChange={(name) => {
              form.setFieldsValue({ type_dag: name });

              if (isNew) {
                // select diensten and registration lists from template to fill out below
                const selectedTemplate = templates.find(
                  (template) => template.name === name,
                );
                setSelectedTemplate(selectedTemplate);
                setDienstenState(selectedTemplate?.diensten || []);
                setRegistrationListsState(
                  selectedTemplate?.registration_lists || [],
                );
                setChecklistStart(selectedTemplate?.checklist_start_day || []);
                setChecklistEnd(selectedTemplate?.checklist_end_day || []);
              }
            }}
          >
            {templates?.map((template) => (
              <Select.Option key={template.name} value={template.name}>
                {template.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={$t("Beschrijving")}
          name="titel"
          rules={[
            {
              required: false,
              message: $t("Maximaal ${c} characters", { c: 100 }),
              max: 110,
            },
          ]}
        >
          <Input />
        </Form.Item>

        {clubHasFlight() && (
          <Form.Item label={$t("Vliegend")} name="is_vliegend">
            <Switch
              onChange={(checked) => setVliegend(checked)}
              checked={vliegend}
              checkedChildren={$t("Ja")}
              unCheckedChildren={$t("Nee")}
            />
          </Form.Item>
        )}

        <FlightLocationFields
          vliegend={vliegend}
          atThuisveld={atThuisveld}
          setAtThuisVeld={setAtThuisVeld}
          vliegveld={vliegveld}
          onSelectAirport={(airportCode) => {
            form.setFieldsValue({ locatie: airportCode });
          }}
        />

        <Form.Item
          label={$t("Briefing tijd")}
          name="briefing_tijd"
          rules={[{ required: true, message: $t("Voeg tijd toe!") }]}
        >
          <Input type="time" style={{ width: 120 }} placeholder="hh:mm" />
        </Form.Item>

        {clubHasFinance() &&
          (profile.wijzig_financial || profile.wijzig_voucher) && (
            <Form.Item
              label={$t("Accepteer bonnen")}
              name="accept_vouchers"
              valuePropName="checked"
              extra={$t("(Tijdelijk) uitzetten verkoop bonnen")}
            >
              <Switch
                checkedChildren={$t("Ja")}
                unCheckedChildren={$t("Nee")}
              />
            </Form.Item>
          )}

        {activeDay && (
          <Form.Item
            label={$t("Gaat door")}
            name="gaat_door"
            valuePropName="checked"
            extra={$t(
              "Indien de dag niet doorgaat ontvangen alle aangemelde leden een email.",
            )}
          >
            <Switch checkedChildren={$t("Ja")} unCheckedChildren={$t("Nee")} />
          </Form.Item>
        )}
      </Form>

      {wijzigRooster && selectedTemplate && isNew && (
        <Tabs
          defaultActiveKey="1"
          items={[
            {
              key: "2",
              label: $t("Diensten"),
              children: (
                <DienstTemplate
                  hasAutofill={false}
                  value={diensten}
                  onChange={setDienstenState}
                />
              ),
            },
            {
              key: "3",
              label: $t("Registratie lijsten"),
              children: (
                <RegistrationListTemplate
                  value={registration_lists}
                  onChange={setRegistrationListsState}
                />
              ),
            },
            {
              key: "4",
              label: $t("Checklists"),
              children: (
                <ChecklistEditor
                  checklistStart={checklistStart}
                  setChecklistStart={setChecklistStart}
                  checklistEnd={checklistEnd}
                  setChecklistEnd={setChecklistEnd}
                />
              ),
            },
          ]}
        />
      )}

      {wijzigRooster && !isNew && (
        <div>
          <p className="subtitle">{$t("Registratielijsten")}</p>

          <RegistrationListTemplate
            value={registration_lists}
            onChange={setRegistrationListsState}
          />

          <p className="subtitle" style={{ marginTop: 16 }}>
            {$t("Start checklist")}
          </p>
          <ChecklistTemplateEditor
            value={checklistStart}
            onChange={setChecklistStart}
          />

          <p className="subtitle" style={{ marginTop: 16 }}>
            {$t("Einde checklist")}
          </p>
          <ChecklistTemplateEditor
            value={checklistEnd}
            onChange={setChecklistEnd}
          />
        </div>
      )}
    </Modal>
  );
}
